import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { BUTTON_VARIANT, BUTTON_SIZE } from '$lib/constants/constants';

enum BLOCKS {
  DOCUMENT = 'document',
  PARAGRAPH = 'paragraph',
  HEADING_1 = 'heading-1',
  HEADING_2 = 'heading-2',
  HEADING_3 = 'heading-3',
  HEADING_4 = 'heading-4',
  HEADING_5 = 'heading-5',
  HEADING_6 = 'heading-6',
  OL_LIST = 'ordered-list',
  UL_LIST = 'unordered-list',
  LIST_ITEM = 'list-item',
  HR = 'hr',
  QUOTE = 'blockquote',
  EMBEDDED_ENTRY = 'embedded-entry-block',
  EMBEDDED_ASSET = 'embedded-asset-block',
  EMBEDDED_RESOURCE = 'embedded-resource-block',
  TABLE = 'table',
  TABLE_ROW = 'table-row',
  TABLE_CELL = 'table-cell',
  TABLE_HEADER_CELL = 'table-header-cell',
  HYPERLINK = 'hyperlink',
}

enum MARKS {
  BOLD = 'bold',
  ITALIC = 'italic',
  UNDERLINE = 'underline',
  CODE = 'code',
  SUPERSCRIPT = 'superscript',
  SUBSCRIPT = 'subscript',
}

let commonClass = ``;

const options = {
  preserveWhitespace: true,
  renderMark: {
    [MARKS.BOLD]: (text: string) => `<strong>${text}</strong>`,
    [MARKS.ITALIC]: (text: string) => `<i>${text}</i>`,
    [MARKS.UNDERLINE]: (text: string) => `<u>${text}</u>`,
    [MARKS.CODE]: (text: string) => `<code>${text}</code>`,
    [MARKS.SUPERSCRIPT]: (text: string) => `<sup>${text}</sup>`,
    [MARKS.SUBSCRIPT]: (text: string) => `<sub>${text}</sub>`,
  },
  renderNode: {
    // Added whitespace character after each paragraph to ensure it renders and so will display new lines
    // Enables web to display new lines similar to the rich text editor in Contentful
    [BLOCKS.PARAGRAPH]: (node: any, next: any) =>
      `<p class='${commonClass} font-medium text-md lg:text-lg'>${next(node.content)}</p>`,
    [BLOCKS.HEADING_1]: (node: any, next: any) =>
      `<h1 class='${commonClass} text-gray-900 text-display-md lg:text-display-2xl font-bold'>${next(
        node.content,
      )}</h1>`,
    [BLOCKS.HEADING_2]: (node: any, next: any) =>
      `<h2 class='${commonClass} text-gray-900 text-display-xs lg:text-display-sm font-bold'>${next(
        node.content,
      )}</h2>`,
    [BLOCKS.HEADING_3]: (node: any, next: any) =>
      `<h3 class='${commonClass} text-gray-900 text-display-xxs lg:text-display-xs font-bold'>${next(
        node.content,
      )}</h3>`,
    [BLOCKS.HEADING_4]: (node: any, next: any) =>
      `<h4 class='${commonClass} text-gray-900 text-xl lg:text-display-xxs font-bold'>${next(
        node.content,
      )}</h4>`,
    [BLOCKS.HEADING_5]: (node: any, next: any) =>
      `<h5 class='${commonClass} text-gray-900 text-17 lg:text-xl font-bold'>${next(
        node.content,
      )}</h5>`,
    [BLOCKS.HEADING_6]: (node: any, next: any) =>
      `<h6 class='${commonClass} text-gray-900 text-15 font-medium'>${next(node.content)}</h6>`,
    [BLOCKS.HYPERLINK]: (node: any, next: any) =>
      `<a class='text-primary-700 underline inline'
      href=${node.data.uri}>${next(node.content)}</a>`,
    [BLOCKS.QUOTE]: (node: any, next: any) =>
      `<p class="text-xl lg:text-display-xxs text-gray-900 font-semibold">${next(
        node.content?.[0].content,
      )}</p>`,
    [BLOCKS.HR]: () => `<div class="w-full border border-text-200 mt-8"></div>`,
    [BLOCKS.OL_LIST]: (node: any, next: any) =>
      `<ol class='${commonClass} list-decimal pl-4'>${next(node.content)}</ol>`,
    [BLOCKS.UL_LIST]: (node: any, next: any) =>
      `<ul class='${commonClass} list-disc pl-4'>${next(node.content)}</ul>`,
    [BLOCKS.LIST_ITEM]: (node: any, next: any) =>
      `<li class='${commonClass} text-left'>${next(node.content)}</li>`,
    [BLOCKS.TABLE]: (node: any, next: any) =>
      `<table class='${commonClass} table-auto w-full'>${next(node.content)}</table>`,
    [BLOCKS.TABLE_ROW]: (node: any, next: any) =>
      `<tr class='${commonClass}'>${next(node.content)}</tr>`,
    [BLOCKS.TABLE_CELL]: (node: any, next: any) =>
      `<td class='${commonClass} border'>${next(node.content?.[0]?.content)}</td>`,
    [BLOCKS.TABLE_HEADER_CELL]: (node: any, next: any) =>
      `<th class='${commonClass} border font-medium'>${next(node.content?.[0]?.content)}</th>`,
    [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
      const asset = assetMap.get(node.data.target.sys.id);
      return `<img src=${asset?.url} alt=${
        asset?.description || asset.title
      } class='rounded-xl w-full' />`;
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
      const entry = entryMap.get(node.data.target.sys.id);
      const buttonClass =
        'border focus:outline-0 focus:shadow-button font-semibold rounded-full flex flex-row justify-center items-center';
      let sizeClass = '';
      let variantClass = '';

      switch (entry?.buttonSize) {
        case BUTTON_SIZE.LARGE:
          sizeClass = 'px-8 py-2.5 ';
          break;
        case BUTTON_SIZE.MEDIUM:
          sizeClass = 'px-6 py-2.5 text-sm';
          break;
      }

      switch (entry?.variant) {
        case BUTTON_VARIANT.PRIMARY:
          variantClass =
            'bg-primary-500 border-primary-500 text-white hover:bg-primary-700 hover:border-primary-700 focus:primary-shadow disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-300';
          break;
        case BUTTON_VARIANT.OUTLINED:
          variantClass =
            'bg-white border-gray-500 text-gray-700 hover:bg-gray-50 hover:text-gray-800 focus:shadow-gray-100/95 disabled:text-gray-300 disabled:border-gray-200';
          break;
        case BUTTON_VARIANT.SECONDARY:
          variantClass =
            'bg-aubergine-button border-aubergine-button text-white hover:bg-aubergine-hover hover:border-aubergine-hover focus:shadow-aubergine-shadow disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-300';
      }

      return `
        <div class='w-max'>
          <a
            class='${buttonClass} ${sizeClass} ${variantClass} plausible-event-name=webColumn_CTA_${entry?.text?.split(' ').join('_')}'
            href=${entry?.url}
            aria-label="${entry?.text}"
            target='${entry?.openInNewTab ? '_blank' : '_self'}'
          >
            ${entry?.text}
          </a>
        </div>
        `;
    },
  },
};

const assetMap = new Map();
const entryMap = new Map();

export const richTextFormatter = (content, newColor = 'text-gray-600') => {
  commonClass = `${newColor}`;

  content?.links?.assets?.block?.forEach((asset) => {
    assetMap.set(asset.sys.id, asset);
  });

  content?.links?.entries?.block?.forEach((entry) => {
    entryMap.set(entry.sys.id, entry);
  });

  const html = documentToHtmlString(content?.json, options);

  return html;
};
